import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import SectionHeading from "../components/SectionHeading";
import { useAuth } from "../context/AuthContext";
import { labGuideMeta, products } from "../config/products";
import "./Lab.css";

function Lab() {
  const { user } = useAuth();
  const [search, setSearch] = useState("");

  const filtered = search.trim()
    ? labGuideMeta.filter((g) => {
        const q = search.toLowerCase();
        return (
          g.title.toLowerCase().includes(q) ||
          g.excerpt.toLowerCase().includes(q) ||
          (g.tags && g.tags.some((t) => t.toLowerCase().includes(q)))
        );
      })
    : labGuideMeta;

  const isSignedIn = !!user;
  const previewGuides = labGuideMeta.slice(0, 1);
  const visibleGuides = isSignedIn ? filtered : previewGuides;

  return (
    <>
      <Helmet>
        <title>The Lab — Guides & Knowledge | AxiomLabs Solutions</title>
        <meta
          name="description"
          content="Practical how-to guides and deep dives from the practitioners at AxiomLabs. Security, AI, automation, and compliance tutorials."
        />
      </Helmet>
      <div className="lab-page">
        <div className="lab-hero">
          <div className="container">
            <SectionHeading
              eyebrow="The Lab"
              title="Guides & Knowledge"
              subheading="Practical how-to guides and deep dives from the practitioners at AxiomLabs."
            />
            <div className="lab-search">
              <div className="lab-search__wrapper">
                <span className="lab-search__icon">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                </span>
                <input
                  className="lab-search__input"
                  type="text"
                  placeholder="Search guides, tutorials, and resources..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="container">
          <div className="lab-grid">
            {visibleGuides.map((guide) => (
              <div key={guide.id} className="lab-card">
                {guide.featuredImage && (
                  <div className="lab-card__image">
                    <img src={guide.featuredImage} alt={guide.title} />
                  </div>
                )}
                <div className="lab-card__meta">
                  <span className="lab-card__badge">Guide</span>
                  <span className="lab-card__time">{guide.readTime}</span>
                </div>
                <h2 className="lab-card__title">{guide.title}</h2>
                <p className="lab-card__text">{guide.excerpt}</p>

                {guide.tags && (
                  <div className="lab-card__tags">
                    {guide.tags.map((tag) => (
                      <span key={tag} className="lab-card__tag">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                <div className="lab-card__actions">
                  <Link to={`/lab/${guide.slug}`} className="lab-card__btn">
                    <span>Learn More</span>
                    <span className="lab-card__arrow">→</span>
                  </Link>

                  {guide.productSlug &&
                    isSignedIn &&
                    (() => {
                      const product = products.find(
                        (p) => p.slug === guide.productSlug,
                      );
                      const isFree = product && product.delivery === "free";
                      if (isFree) return null;
                      return (
                        <Link
                          to={`/checkout?currency=EUR&product=${guide.productSlug}`}
                          className="lab-card__buy"
                        >
                          Get This Tool →
                        </Link>
                      );
                    })()}
                </div>
              </div>
            ))}

            {filtered.length === 0 && search.trim() && (
              <div className="lab-empty">
                <p>No guides found for "{search}"</p>
                <button className="lab-card__btn" onClick={() => setSearch("")}>
                  Clear Search
                </button>
              </div>
            )}

            {!isSignedIn && labGuideMeta.length > 1 && (
              <div className="lab-gate">
                <div className="lab-gate__inner">
                  <span className="lab-gate__icon">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0110 0v4" />
                    </svg>
                  </span>
                  <h3 className="lab-gate__title">
                    {labGuideMeta.length - 1} More{" "}
                    {labGuideMeta.length - 1 === 1 ? "Guide" : "Guides"}{" "}
                    Available
                  </h3>
                  <p className="lab-gate__text">
                    Sign in to unlock all tutorials, voice libraries, and
                    step-by-step guides.
                  </p>
                  <Link to="/auth" className="lab-gate__btn">
                    Sign In to Access All Guides
                  </Link>
                  <p className="lab-gate__hint">
                    Free account — no purchase required.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default Lab;
